"use client";

import type { AudioSample, Media, SantaContentBlock } from "@mw/shared/payload-types";

import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { songColorToThumbnailImage } from "@/app/(pages)/[locale]/(full-screen)/music/songs/[slug]/colorToImage";
import { AudioListProvider } from "@/app/_providers/audio-list";
import { Button } from "@ui/components/core/Button";
import { CornerMarkerDot } from "@ui/components/core/CornerMarkerDot";
import { Image } from "@ui/components/core/Image";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";
import { useWavesurfer } from "@ui/hooks/useWavesurferPlayer";
import { cn } from "@ui/lib/utils";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { getAudioUrl } from "@utilities/payload";
import { PauseIcon, PlayIcon } from "lucide-react";
import { appUrls } from "@/constants";

interface SongMetadata {
  audioUrl: string;
  category?: string;
  colorScheme: MusicColorScheme;
  tags: string[];
  title: string;
}

// Video Generator Card Component
function VideoGeneratorCard({
  title,
  inputPlaceholder,
  buttonText,
  variant,
}: {
  title?: string | null;
  inputPlaceholder?: string | null;
  buttonText?: string | null;
  variant?: string | null;
}) {
  const textContentRef = useRef<HTMLTextAreaElement>(null);
  const [hasContent, setHasContent] = useState(false);
  const router = useRouter();
  const t = useTranslations("BlockSantaContent");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const textContent = textContentRef.current?.value ?? "";
    if (!textContent.trim()) {
      return;
    }

    const encodedScript = encodeURIComponent(textContent);
    const url = `${appUrls.templates}?workflow=lip-sync&variant=${variant || "santa"}&stepData.0.text=${encodedScript}`;

    router.push(url);
  };

  const handleInput = () => {
    const hasText = Boolean(textContentRef.current?.value.trim());
    if (hasText !== hasContent) {
      setHasContent(hasText);
    }
  };

  return (
    <div className='mt-24 flex flex-col rounded-xxl bg-default p-32 shadow-hairline md:mt-0'>
      {/* Title */}
      {title && <h3 className='f-heading-04 mb-24'>{title}</h3>}

      {/* Divider */}
      <div className='relative mb-24 h-[2px]'>
        <DottedBorder side='top' color='rgba(0,0,0,0.15)' />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className='flex h-full flex-col'>
        {/* Text Input */}
        <textarea
          ref={textContentRef}
          onInput={handleInput}
          placeholder={inputPlaceholder || t("video_card_placeholder")}
          className='bg-transparent f-paragraph-02 mb-32 w-full flex-1 resize-none border-none p-0 text-default placeholder:text-light focus:outline-none focus:ring-0'
        />

        {/* Generate Button */}
        <div>
          <Button type='submit' disabled={!hasContent} size='lg'>
            {buttonText || "Generate"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// Song List Component
function SongList({
  songs,
  activeSong,
  isPlaying,
  onSelect,
}: {
  activeSong: SongMetadata | null;
  isPlaying: boolean;
  onSelect: (song: SongMetadata) => void;
  songs: SongMetadata[];
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [visibleOverlays, setVisibleOverlays] = useState<{ bottom: boolean; top: boolean }>({
    bottom: true,
    top: false,
  });

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
          setVisibleOverlays({ bottom: scrollTop < scrollHeight - clientHeight - 10, top: scrollTop > 0 });
        }
      });
    }
  }, []);

  useEffect(() => {
    handleScroll();

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [handleScroll, songs]);

  return (
    <div className='relative flex min-h-0 flex-1 flex-col'>
      <div className='scrollbar-none min-h-0 flex-1 overflow-y-auto px-32 pb-16' ref={scrollContainerRef}>
        {songs.map((song, index) => {
          const isActive = song.audioUrl === activeSong?.audioUrl;
          const isActivePlaying = isActive && isPlaying;
          const isFirst = index === 0;

          return (
            <div key={song.audioUrl}>
              {/* Dotted separator */}
              {!isFirst && (
                <div className='relative mx-12 h-[2px]'>
                  <DottedBorder side='top' color='rgba(0,0,0,0.12)' />
                </div>
              )}
              <button
                className={cn(
                  "group flex w-full items-center justify-between gap-12 rounded-xl p-12 text-left transition-all",
                  isActive
                    ? "border border-[rgba(0,0,0,0.07)] bg-eggshell shadow-outline"
                    : "border border-transparent hover:bg-[rgba(0,0,0,0.03)]",
                )}
                onClick={() => onSelect(song)}
              >
                <div className='flex gap-12'>
                  <Image
                    alt={song.title}
                    className='h-40 w-40 rounded-md'
                    height={960}
                    sizes='42px'
                    src={songColorToThumbnailImage[song.colorScheme]}
                    width={960}
                  />
                  <div className='flex flex-col'>
                    <p className='f-paragraph-03 mb-2'>{song.title}</p>
                    {song.category && <p className='f-paragraph-04 mb-2 text-light'>{song.category}</p>}
                    {song.tags.length > 0 && <p className='f-paragraph-04 text-lighter'>{song.tags.join(", ")}</p>}
                  </div>
                </div>
                <div
                  className={cn(
                    "flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-full bg-inverse p-4 text-default transition-opacity",
                    isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                  )}
                >
                  {isActivePlaying ? (
                    <PauseIcon className='text-inverse' fill='currentColor' size={16} />
                  ) : (
                    <PlayIcon className='text-inverse' fill='currentColor' size={16} />
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
      <div
        className={cn(
          "pointer-events-none absolute top-0 z-3 h-40 w-full bg-gradient-to-b from-[#F5F3F1] to-transparent transition-opacity duration-200",
          visibleOverlays.top ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute bottom-0 z-3 h-40 w-full bg-gradient-to-t from-[#F5F3F1] to-transparent transition-opacity duration-200",
          visibleOverlays.bottom ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

// Music Player Card Component
function MusicPlayerCard({
  title,
  songs,
}: {
  title?: string | null;
  songs?: NonNullable<SantaContentBlock["musicCard"]>["songs"];
}) {
  const songItems: SongMetadata[] = useMemo(() => {
    if (!songs) return [];

    return songs
      .map(item => {
        // Handle relationship - item could be a string ID or an AudioSample object
        if (typeof item === "string") return null;
        const audioSample = item as AudioSample;

        const audioUrl = getAudioUrl(audioSample.audio as Media);
        if (!audioUrl) return null;

        const colorScheme = (audioSample.orbColor || "red") as MusicColorScheme;
        const tags = audioSample.styleTags
          ? audioSample.styleTags.map(tagItem => tagItem.tag).filter((tag): tag is string => Boolean(tag))
          : [];

        const song: SongMetadata = {
          audioUrl,
          colorScheme,
          tags,
          title: audioSample.title || "Untitled",
        };

        if (audioSample.subtitle) {
          song.category = audioSample.subtitle;
        }

        return song;
      })
      .filter((item): item is SongMetadata => item !== null);
  }, [songs]);

  const [activeSong, setActiveSong] = useState<SongMetadata | null>(songItems[0] || null);
  const pendingPlayRef = useRef(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  const { isPlaying, play, togglePlayPause, mediaElement } = useWavesurfer({
    url: activeSong?.audioUrl || "",
    timelineRef,
    id: activeSong?.audioUrl || "default",
    height: 32,
    waveColor: "lightgray",
    progressColor: "#030712",
  });

  // When mediaElement is ready and we have a pending play, trigger it
  useEffect(() => {
    if (!mediaElement || !pendingPlayRef.current) return;

    const handleCanPlay = () => {
      if (pendingPlayRef.current) {
        play();
        pendingPlayRef.current = false;
      }
    };

    // If already ready to play (readyState >= HAVE_FUTURE_DATA)
    if (mediaElement.readyState >= 3) {
      handleCanPlay();
      return;
    }

    mediaElement.addEventListener("canplay", handleCanPlay);
    return () => mediaElement.removeEventListener("canplay", handleCanPlay);
  }, [mediaElement, play]);

  // Auto-play next song when current one ends
  useEffect(() => {
    if (!mediaElement) return;

    const handleEnded = () => {
      const currentIndex = songItems.findIndex(s => s.audioUrl === activeSong?.audioUrl);
      const nextSong = songItems[currentIndex + 1];
      if (nextSong) {
        pendingPlayRef.current = true;
        setActiveSong(nextSong);
      }
    };

    mediaElement.addEventListener("ended", handleEnded);
    return () => mediaElement.removeEventListener("ended", handleEnded);
  }, [mediaElement, activeSong, songItems]);

  const handleSelectSong = (song: SongMetadata) => {
    if (song.audioUrl === activeSong?.audioUrl) {
      togglePlayPause();
      return;
    }

    pendingPlayRef.current = true;
    setActiveSong(song);
  };

  if (songItems.length === 0) {
    return null;
  }

  return (
    <div className='mb-24 overflow-hidden rounded-xxl bg-dark-new pb-0 pt-32 md:mb-0'>
      {title && <h3 className='f-heading-04 mb-16 pl-44'>{title}</h3>}

      <div className='flex max-h-[400px] flex-col'>
        <SongList activeSong={activeSong} isPlaying={isPlaying} onSelect={handleSelectSong} songs={songItems} />
      </div>

      {/* Hidden timeline for wavesurfer */}
      <div className='hidden' ref={timelineRef} />
    </div>
  );
}

// Separator Component
function Separator() {
  return (
    <div className='absolute left-1/2 top-[-2px] flex h-full items-stretch justify-center'>
      {/* Mobile - Horizontal separator */}
      <div className='relative w-full border-t border-overlay-dark lg:hidden'>
        <div className='absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2'>
          <CornerMarkerDot backgroundColor='white' />
        </div>
        <div className='absolute right-0 top-0 -translate-y-1/2 translate-x-1/2'>
          <CornerMarkerDot backgroundColor='white' />
        </div>
      </div>

      {/* Desktop - Vertical separator */}
      <div className='relative hidden w-0 border-l border-overlay-dark lg:block'>
        <CornerMarkerDot backgroundColor='white' position='topCentered' />
        <CornerMarkerDot backgroundColor='white' position='bottomCentered' />
      </div>
    </div>
  );
}

// Main Combined Component
function SantaContentInner({ videoCard, musicCard }: SantaContentBlock) {
  return (
    <div className='grid grid-cols-1 gap-24 lg:grid-cols-[1fr_1fr] lg:gap-32'>
      {/* Left Column - Video Generator */}
      <VideoGeneratorCard
        title={videoCard?.title}
        inputPlaceholder={videoCard?.placeholder}
        buttonText={videoCard?.buttonText}
        variant={videoCard?.variant}
      />

      {/* Separator */}
      <Separator />

      {/* Right Column - Music Player */}
      <MusicPlayerCard title={musicCard?.title} songs={musicCard?.songs} />
    </div>
  );
}

export default function BlockSantaContent(props: SantaContentBlock) {
  return (
    <AudioListProvider>
      <section className='container relative py-6 md:py-12'>
        <SantaContentInner {...props} />
      </section>
    </AudioListProvider>
  );
}
